import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileCheck2, ShieldCheck } from "lucide-react";

const formatDateLong = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "long", year: "numeric" }).format(date);
};

const decodePublicPayload = () => {
  const raw = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("data");
  if (!raw) return null;
  try {
    const base64 = raw.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(raw.length / 4) * 4, "=");
    return JSON.parse(decodeURIComponent(escape(atob(base64))));
  } catch {
    return null;
  }
};

const QR_PUBLIC_BASE_URL = (
  process.env.REACT_APP_QR_PUBLIC_BASE_URL || "https://notaris-erwin.pages.dev"
).replace(/\/+$/, "");

const publicQrApiUrl = (id) => {
  const isLocal =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const baseUrl = isLocal ? QR_PUBLIC_BASE_URL : window.location.origin;
  return `${baseUrl}/api/qrs/public/${encodeURIComponent(id)}`;
};

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 border-b border-ink/10 py-3 last:border-0">
      <span className="text-[#606B75]">{label}</span>
      <span className="font-medium text-ink">: {value}</span>
    </div>
  );
}

export default function QRVerify() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const parties = Array.isArray(data?.parties) ? data.parties : [];
  const photos = Array.isArray(data?.photos) ? data.photos : [];

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    const embeddedData = decodePublicPayload();
    fetch(publicQrApiUrl(id))
      .then((response) => {
        if (!response.ok) throw new Error("QR tidak ditemukan");
        return response.json();
      })
      .then((payload) => {
        if (alive) setData(payload);
      })
      .catch(() => {
        if (alive) {
          setData(embeddedData);
          setNotFound(!embeddedData);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <div className="min-h-screen bg-[#F9F8F4] px-4 py-8 text-ink">
      <main className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gold text-white">
            <FileCheck2 className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-2xl font-semibold">Arsip Notaris</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Digital Verification</div>
          </div>
        </div>

        <section className="relative overflow-hidden rounded-xl border border-borderc bg-white p-6 shadow-sm sm:p-8">
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="-rotate-12 select-none text-5xl font-bold uppercase tracking-[0.16em] text-ink/[0.035] sm:text-7xl">
              DOKUMEN ASLI
            </div>
          </div>

          <div className="relative">
            <div className="mb-6 flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-emerald2/10 text-emerald2">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-gold">Status QR</div>
                <h1 className="font-display text-4xl font-medium text-ink">Verifikasi Dokumen</h1>
                <p className="mt-1 text-sm text-[#606B75]">
                  Halaman ini menampilkan watermark/disclaimer dokumen yang dibuat melalui kantor Notaris/PPAT Erwin Suhardiman.
                </p>
              </div>
            </div>

            <div className="mb-5 rounded-lg border border-emerald2/20 bg-emerald2/5 px-4 py-3 text-sm text-emerald2">
              QR ID: <strong>{id}</strong>
            </div>

            {loading && (
              <div className="mb-5 rounded-lg border border-borderc bg-[#F9F8F4] px-4 py-3 text-sm text-[#606B75]">
                Memuat detail verifikasi...
              </div>
            )}

            <div className="rounded-lg border border-borderc bg-[#F9F8F4] px-4 text-sm">
              <InfoRow label="Nomor Akta" value={data?.deedNumber || "-"} />
              <InfoRow label="Dokumen" value={data?.documentTitle || "-"} />
              <InfoRow label="Client" value={data?.clientName || "-"} />
              <InfoRow label="Tanggal" value={formatDateLong(data?.documentDate)} />
              <InfoRow label="Staff" value={data?.staffName || "-"} />
            </div>

            {parties.length > 0 && (
              <div className="mt-5 rounded-lg border border-borderc bg-[#F9F8F4] p-4 text-sm">
                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gold">Pihak Terkait</div>
                <div className="space-y-3">
                  {parties.map((party, index) => (
                    <div key={`${party.label}-${index}`} className="rounded-lg border border-ink/10 bg-white p-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#606B75]">
                        {party.label || `Pihak ${index + 1}`}
                      </div>
                      <div className="mt-1 font-semibold text-ink">{party.name || "-"}</div>
                      {party.description && <div className="mt-1 whitespace-pre-line text-xs leading-relaxed text-[#606B75]">{party.description}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {photos.length > 0 && (
              <div className="mt-5 rounded-lg border border-borderc bg-[#F9F8F4] p-4 text-sm">
                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gold">Foto Pendukung</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {photos.map((photo, index) => (
                    <figure key={photo.id || index} className="overflow-hidden rounded-lg border border-ink/10 bg-white">
                      <img src={photo.dataUrl} alt={photo.caption || photo.name || `Foto ${index + 1}`} className="aspect-[4/3] w-full object-cover" />
                      <figcaption className="p-2 text-xs text-[#606B75]">{photo.caption || photo.name || `Foto ${index + 1}`}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 rounded-lg border border-gold/30 bg-gold/10 p-4 text-sm leading-relaxed">
              <strong>Disclaimer:</strong> QR ini adalah penanda verifikasi/watermark digital. Keaslian dokumen tetap
              mengacu pada dokumen fisik/resmi, tanda tangan, cap, nomor akta, serta arsip internal kantor.
            </div>

            {data?.note && <p className="mt-5 whitespace-pre-line text-sm text-[#606B75]">{data.note}</p>}

            {notFound && (
              <p className="mt-5 text-sm text-[#606B75]">
                Detail QR tidak ditemukan. Jika QR berasal dari dokumen resmi, cocokkan QR ID dengan arsip kantor.
              </p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
